import { cloneDeep } from 'lodash-es';
import { useEffect, useRef, useState } from 'react';

import { DEFAULT_ERROR_MESSAGE } from '../Components/utils';

import { useToast } from '../ToastProvider/ToastStackProvider';
import { ToastVariant } from '../ToastProvider/types';
import { IFavoritesSection, IMenuItem, IMenuOption, ISubmenuSection, IUpdatedIdOrder } from '../types';

export interface IUseControlsProps {
  menuOptions: IMenuOption[];
  favorites?: IFavoritesSection[];
  hideOpenGovLogo?: boolean;
  openGovLogoUrl?: string;
  openGovLogoLinkComponent?: React.ElementType;
  openGovLogoOnClick?: () => void;
  getSubmenuItems?(menuLink: IMenuOption | IMenuItem): Promise<ISubmenuSection[]>;
  onFavoriteReorder?(updatedIdOrder: IUpdatedIdOrder): Promise<string[]>;
  onFavoriteSaveClick?(menuLink: IMenuItem): Promise<IMenuItem>;
  onFavoriteDeleteClick?(menuLink: IMenuItem): Promise<IMenuItem>;
}

export const useControls = ({
  menuOptions,
  favorites: propFavorites,
  hideOpenGovLogo,
  openGovLogoUrl,
  openGovLogoLinkComponent,
  openGovLogoOnClick,
  getSubmenuItems,
  onFavoriteReorder,
  onFavoriteSaveClick,
  onFavoriteDeleteClick: handleFavoriteDeleteClick
}: IUseControlsProps) => {
  const [menuItems, setMenuItems] = useState<IMenuOption[]>(menuOptions);
  useEffect(() => {
    setMenuItems(menuOptions);
  }, [menuOptions]);

  const [favorites, setFavorites] = useState<IFavoritesSection[]>(propFavorites);

  useEffect(() => {
    if (propFavorites && Array.isArray(propFavorites)) {
      const favoritesWithLabels = propFavorites?.map((fav) => {
        const label = menuItems?.find((item) => item.id === fav.id)?.label;
        return { label, ...fav };
      });
      if (favoritesWithLabels.length > 0) {
        setFavorites(favoritesWithLabels);
      }
      if (favoritesWithLabels.length === 0 && favorites.length > 0) {
        setFavorites([]);
      }
    }
  }, [propFavorites, menuItems?.length]);

  // favoritesRefs is a 2D array of refs, where the first dimension represents the index of the favorites section
  const [favoritesRefs, setFavoritesRefs] = useState<React.RefObject<HTMLDivElement>[][]>(favorites?.map(() => []));
  // This state is used to focus the first favorite when the favorites panel is opened
  const [focusFirstFavoriteWhenItLoads, setFocusFirstFavoriteWhenItLoads] = useState(false);

  const [selectedLevelTwoId, setSelectedLevelTwoId] = useState<string | null>(null);
  const [currentLevelOneItem, setCurrentLevelOneItem] = useState<IMenuOption | null>(null);

  const currentLevelOneItemRef = useRef<HTMLLIElement | null>(null);
  const currentLevelTwoItemRef = useRef<HTMLLIElement | null>(null);
  const currentFirstLevelThreeItemRef = useRef<HTMLLIElement | null>(null);
  const currentFirstLevelTwoItemRef = useRef<any>(null);

  // If submenu sections need to be fetched, this tracks the id of the parent item doing the fetching
  const [submenuLoadingItemId, setSubmenuLoadingItemId] = useState<string>();

  const [levelTwoLoadingError, setLevelTwoLoadingError] = useState<boolean>(false);
  const [levelThreeLoadingError, setLevelThreeLoadingError] = useState<boolean>(false);
  const [customErrorMessage, setCustomErrorMessage] = useState<string | null>(null);

  const [showMainSubmenu, setShowMainSubmenu] = useState(false);
  const [showFavoritesSubmenu, setShowFavoritesSubmenu] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  const toaster = useToast();

  // When the 'showFavoritesSubmenu' state becomes true, indicating the favorites submenu is opened,
  // 'focusFirstFavoriteWhenItLoads' is set to true to prepare for focusing the first favorite item.
  useEffect(() => {
    if (showFavoritesSubmenu) {
      setFocusFirstFavoriteWhenItLoads(true);
    } else {
      setFocusFirstFavoriteWhenItLoads(false);
    }
  }, [showFavoritesSubmenu]);

  // This effect waits for the first favorite item to be ready (i.e., its ref is defined),
  // and when it is, it sets the focus on this item and then sets 'focusFirstFavoriteWhenItLoads' back to false.
  // This ensures that the first item is focused only when the favorites submenu is opened,
  // and not when items are moved around or deleted.
  useEffect(() => {
    if (focusFirstFavoriteWhenItLoads && favoritesRefs?.[0]?.[0]?.current) {
      favoritesRefs[0][0].current.focus();
      setFocusFirstFavoriteWhenItLoads(false);
    }
  }, [favoritesRefs]);

  useEffect(() => {
    // Each time the current level one item is removed,
    // reset related child component state values
    // i.e. selected level two id, and L2/L3 loading & error states
    if (!currentLevelOneItem) {
      setSelectedLevelTwoId(null);

      setSubmenuLoadingItemId(undefined);

      setLevelTwoLoadingError(false);
      setLevelThreeLoadingError(false);
    }
  }, [currentLevelOneItem]);

  // Fetches submenu sections for a level one menu item
  // In addition to returning the fetched L2 sections,
  // it also updates menuItems state to include the fetched sections
  const fetchLevelTwoSubmenuSections = async (levelOneMenuOption: IMenuOption) => {
    if (!getSubmenuItems) {
      console.error('No submenu items fetch function provided');
      return;
    }
    const levelOneParentIndex = menuItems?.findIndex((menuItem) => {
      return menuItem.id === levelOneMenuOption.id;
    });
    return getSubmenuItems(levelOneMenuOption)
      .then(async (fetchedSubmenuSections: ISubmenuSection[]) => {
        setMenuItems((prevMenuItems) => {
          if (prevMenuItems[levelOneParentIndex]) {
            prevMenuItems[levelOneParentIndex].submenuSections = fetchedSubmenuSections;
          }
          return prevMenuItems;
        });

        return Promise.resolve(fetchedSubmenuSections);
      })
      .catch(async (error) => {
        const errorMessage = error || DEFAULT_ERROR_MESSAGE;
        setCustomErrorMessage(errorMessage);
        return Promise.reject(errorMessage);
      });
  };

  // Fetches submenu sections for a level two menu item
  // In addition to returning the fetched L3 sections,
  // it also updates menuItems state to include the fetched sections
  const fetchLevelThreeSubmenuSections = async (levelTwoMenuItem: IMenuItem): Promise<ISubmenuSection[] | void> => {
    if (!getSubmenuItems) {
      console.error('No submenu items fetch function provided');
      return;
    }
    const levelOneParentIndex = menuItems?.findIndex((menuItem) => {
      return menuItem.id === currentLevelOneItem?.id;
    });
    return getSubmenuItems(levelTwoMenuItem)
      .then(async (fetchedSubmenuSections) => {
        setMenuItems((prevMenuItems) => {
          prevMenuItems[levelOneParentIndex]?.submenuSections?.map((submenuSection) => {
            // Find the submenu section that contains the targeted level two menu item
            if (submenuSection?.items?.find((menuItem) => menuItem.id === levelTwoMenuItem.id)) {
              const updateIndex = submenuSection?.items?.findIndex((menuItem) => menuItem.id === levelTwoMenuItem.id);
              // If a match is found, set the value of the submenu section to the fetched submenu sections
              submenuSection.items[updateIndex].submenuSections = fetchedSubmenuSections;
              return submenuSection;
            } else {
              // If no match is found, return the original submenu section unchanged
              return submenuSection;
            }
          });
          return prevMenuItems;
        });

        return Promise.resolve(fetchedSubmenuSections);
      })
      .catch((error) => {
        setLevelThreeLoadingError(true);
        setCustomErrorMessage(error || DEFAULT_ERROR_MESSAGE);
      });
  };

  const reorderFavoriteItems = (items: IMenuItem[], sourceIndex: number, destinationIndex: number) => {
    const clonedReorderedItems = cloneDeep(items);

    const itemToMove = clonedReorderedItems[sourceIndex];
    clonedReorderedItems.splice(sourceIndex, 1);
    clonedReorderedItems.splice(destinationIndex, 0, itemToMove);

    return clonedReorderedItems;
  };

  const onFavoriteItemDragEnd = async (
    sourceIndex: number,
    destinationIndex: number,
    favoriteSection: IFavoritesSection
  ) => {
    const originalFavorites = cloneDeep(favorites);
    const updatedItems = reorderFavoriteItems(favoriteSection.items, sourceIndex, destinationIndex);
    const updatedIdOrder = updatedItems.map((item) => item.id);
    const parentIndex = favorites.findIndex((favorite) => favorite.id === favoriteSection.id);

    favorites[parentIndex].items = updatedItems;
    setFavorites([...favorites]);

    if (onFavoriteReorder) {
      const favoriteSectionWithUpdatedOrder = {
        id: favorites[parentIndex].id,
        updatedIdOrder
      };

      try {
        await onFavoriteReorder(favoriteSectionWithUpdatedOrder);
      } catch {
        setFavorites(originalFavorites);
        toaster.addToast({
          title: 'The stars hit a snag',
          body: 'Favoriting is having an issue. Try again or contact support if the issue persists.',
          variant: ToastVariant.ERROR
        });
      }
    }
  };

  const removeMenuItemFromFavoritesSections = (menuItem: IMenuItem, favoritesSections: IFavoritesSection[]) => {
    return favoritesSections.reduce<IFavoritesSection[]>((acc, section) => {
      // If an ID match is found, filter out the menu item from the section
      const updatedSectionItems = section.items.filter((item) => item.id !== menuItem.id);
      // Only add the section back if it still has items
      if (updatedSectionItems.length > 0) {
        const updatedFavoritesSection = { ...section, items: updatedSectionItems };
        acc.push(updatedFavoritesSection);
      }
      return acc;
    }, []);
  };

  const addMenuItemToFavoritesSections = (
    menuItem: IMenuItem,
    favoritesSections: IFavoritesSection[],
    parentMenuItem: IMenuItem
  ) => {
    // Create a deep clone so the stateful array is not mutated
    const clonedFavoritesSections = cloneDeep(favoritesSections);
    // Check if the section to add to already exists in the favorites array
    const sectionIdExists = clonedFavoritesSections.some((section) => section.id === parentMenuItem.id);
    let updatedSections: IFavoritesSection[];
    if (sectionIdExists) {
      updatedSections = clonedFavoritesSections.map((section) => {
        // When an section ID match is found, add the menu item to the section
        if (section.id === parentMenuItem.id) {
          const updatedSectionItems = [...section.items, menuItem];
          return { ...section, items: updatedSectionItems };
        }
        return section;
      });
    } else {
      // If the section does not exist, create a new section and add the menu item to it
      updatedSections = [
        ...clonedFavoritesSections,
        {
          id: parentMenuItem.id,
          label: parentMenuItem.label,
          items: [menuItem]
        }
      ];
    }
    return updatedSections;
  };

  const onFavoriteDeleteClick = async (menuItem: IMenuItem) => {
    try {
      await handleFavoriteDeleteClick?.(menuItem);
      setFavorites((prevFavorites) => {
        return removeMenuItemFromFavoritesSections(menuItem, prevFavorites);
      });
    } catch (err) {
      console.error(err);
      toaster.addToast({
        title: 'The stars hit a snag',
        body: 'Favoriting is having an issue. Try again or contact support if the issue persists.',
        variant: ToastVariant.ERROR
      });
    }
  };

  const onFavoriteIconClick = async (isFavorited: boolean, menuItem: IMenuItem) => {
    if (isFavorited) {
      try {
        await handleFavoriteDeleteClick?.(menuItem);
        setFavorites((prevFavorites) => {
          return removeMenuItemFromFavoritesSections(menuItem, prevFavorites);
        });
      } catch (err) {
        console.error(err);
        setFavorites((prevFavorites) => {
          return addMenuItemToFavoritesSections(menuItem, prevFavorites, currentLevelOneItem!);
        });
        toaster.addToast({
          title: 'The stars hit a snag',
          body: 'Favoriting is having an issue. Try again or contact support if the issue persists.',
          variant: ToastVariant.ERROR
        });
      }
    } else {
      try {
        // the longer this takes, the more of a gap there is where local state is out of sync with the server
        await onFavoriteSaveClick?.(menuItem);
        setFavorites((prevFavorites) => {
          return addMenuItemToFavoritesSections(menuItem, prevFavorites, currentLevelOneItem!);
        });
      } catch (err) {
        console.error(err);
        setFavorites((prevFavorites) => {
          return removeMenuItemFromFavoritesSections(menuItem, prevFavorites);
        });
        toaster.addToast({
          title: 'The stars hit a snag',
          body: 'Favoriting is having an issue. Try again or contact support if the issue persists.',
          variant: ToastVariant.ERROR
        });
      }
    }
  };
  return {
    // OpenGov logo Options
    hideOpenGovLogo,
    openGovLogoUrl,
    openGovLogoOnClick,
    openGovLogoLinkComponent,
    // Favorites state
    favorites,
    disableFavorites: favorites === undefined,
    onFavoriteItemDragEnd,
    onFavoriteIconClick,
    onFavoriteDeleteClick,
    customErrorMessage,
    // Show/hide menu states
    showMainSubmenu,
    setShowMainSubmenu,
    showFavoritesSubmenu,
    setShowFavoritesSubmenu,
    showMoreMenu,
    setShowMoreMenu,
    // Menu item state
    menuItems,
    currentLevelOneItem,
    setCurrentLevelOneItem,
    selectedLevelTwoId,
    setSelectedLevelTwoId,
    fetchLevelThreeSubmenuSections,
    fetchLevelTwoSubmenuSections,
    // Loading and error states
    submenuLoadingItemId,
    setSubmenuLoadingItemId,
    setLevelTwoLoadingError,
    levelTwoLoadingError,
    levelThreeLoadingError,
    setLevelThreeLoadingError,
    // Keyboard nav/ref state
    currentLevelOneItemRef,
    currentLevelTwoItemRef,
    currentFirstLevelThreeItemRef,
    favoritesRefs,
    setFavoritesRefs,
    currentFirstLevelTwoItemRef
  };
};
